import { useMemo } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Box, Stack, Typography } from '@mui/material';

import { itemIsOpen, sortIntakeItemsByStatus } from '../domain';
import type { IntakeItem, ProviderProfile } from '../types';

import type { IntakeDocumentAccess } from './IntakeItemDetailsDialog';
import { IntakeItemListSection } from './IntakeItemListSection';

interface ProviderProfileSectionProps {
  provider: ProviderProfile;
  documentAccess?: IntakeDocumentAccess;
  onUploadRequest?: (item?: IntakeItem) => void;
}

export function ProviderProfileSection({
  provider,
  documentAccess,
  onUploadRequest,
}: ProviderProfileSectionProps) {
  const onFileItems = useMemo(
    () => sortIntakeItemsByStatus(provider.onFileItems),
    [provider.onFileItems],
  );
  const openItems = useMemo(
    () =>
      sortIntakeItemsByStatus(
        [...provider.neededItems, ...provider.onFileItems].filter(itemIsOpen),
      ),
    [provider.neededItems, provider.onFileItems],
  );
  const completeCount = onFileItems.length;
  const isComplete = openItems.length === 0;

  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: 2 },
        borderRadius: '8px',
        bgcolor: ELIG_TOKENS.card,
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        height: '100%',
        minHeight: 0,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '6px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: ELIG_TOKENS.greenSoft,
            color: ELIG_TOKENS.green,
          }}
        >
          <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 850 }}>
            Provider profile
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Collected once and reused across services
            {completeCount > 0 ? ` · ${completeCount} on file` : ''}
          </Typography>
        </Box>
      </Stack>

      {isComplete ? (
        <Box
          sx={{
            display: 'grid',
            minHeight: 160,
            placeItems: 'center',
            borderRadius: '8px',
            bgcolor: ELIG_TOKENS.greenTint,
            border: `1px solid ${ELIG_TOKENS.line}`,
            px: 2,
            py: 3,
            textAlign: 'center',
          }}
        >
          <Stack spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                bgcolor: ELIG_TOKENS.greenSoft,
                color: ELIG_TOKENS.green,
              }}
            >
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 850 }}>
                Provider information is complete
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Everything on file here can be reused across services.
              </Typography>
            </Box>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1.75}>
          <IntakeItemListSection
            title="What we need"
            items={openItems}
            emptyLabel="Nothing matches this filter."
            compact
            columns={1}
            scroll
            documentAccess={documentAccess}
            onUploadRequest={onUploadRequest}
          />
        </Stack>
      )}
    </Box>
  );
}
